import { useCallStore } from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import { getClientSessionId } from '#features/calls/utils/clientSession';
import { isCallDismissedHere } from '#features/calls/utils/dismissedCalls';
import { closeWebCallNotification } from '#features/calls/utils/webCallNotification';
import type { WsMessage } from '#features/realtime/wsMessageParser';

/**
 * Bridges CALL_* WS frames into the call store. Frames carry messageKey=null on
 * purpose (never toasted); all params come as strings inside data.params.
 * currentUserId disambiguates multi-tab cases (e.g. "answered in another tab").
 */
/** Кадры, после которых звонить уже нечему: ответили, отказались, отменили, не дождались. */
const CALL_OVER = new Set(['CALL_ACCEPTED', 'CALL_REJECTED', 'CALL_CANCELLED', 'CALL_MISSED', 'CALL_ENDED']);

export const applyWsCallEvent = (message: WsMessage, currentUserId: string | null) => {
  const params = message.data?.params;
  const callId = params?.callId;
  if (!callId) {
    return;
  }

  const store = useCallStore.getState();

  if (message.type === 'CALL_INCOMING') {
    const chatId = params.chatId;
    const roomName = params.roomName;
    const callerId = params.callerId ?? message.actor?.userId;
    if (!chatId || !roomName || !callerId) {
      return;
    }

    store.setIncoming({
      callId,
      chatId,
      roomName,
      peer: {
        userId: callerId,
        displayName: message.actor?.displayName || null,
        avatar: toCallAvatarUrl(message.actor?.avatar),
      },
      startedByMe: false,
      withVideo: params.mediaType !== 'audio',
      livekitUrl: null,
      token: null,
      acceptedAt: null,
      // The frame is pushed the moment the server starts ringing us, so its arrival is
      // the ring start to within a network hop.
      ringingSince: Date.now(),
    });
    return;
  }

  // Звонок кончился — снимаем баннер, который мог поставить веб-пуш. Делаем это ДО проверки
  // «этот ли звонок показывает вкладка»: баннер живёт в системе, а не в сторе, и висит даже
  // тогда, когда вкладка о звонке ничего не знает — например, её открыли уже после ринга.
  if (CALL_OVER.has(message.type)) {
    closeWebCallNotification(callId);
  }

  // Every other CALL_* event only applies to the call we are showing.
  if (store.call?.callId !== callId) {
    return;
  }

  switch (message.type) {
    case 'CALL_ACCEPTED':
      if (store.phase === 'outgoing') {
        // Caller side: token was granted by call.start — go live.
        store.setActive();
      } else if (store.phase === 'incoming' && message.actor?.userId === currentUserId) {
        // This tab is still ringing but the user answered in another tab.
        store.setEnded('answered_elsewhere');
      }
      break;
    case 'CALL_REJECTED':
      store.setEnded('declined');
      break;
    case 'CALL_CANCELLED':
      store.setEnded('cancelled');
      break;
    case 'CALL_MISSED':
      store.setEnded(store.phase === 'outgoing' ? 'no_answer' : 'missed');
      break;
    case 'CALL_ENDED':
      store.setEnded('ended');
      break;
    case 'CALL_MOVED':
      // Разговор забрало другое наше устройство. Отпускаем комнату (иначе останемся в ней
      // вторым клиентом с открытым микрофоном — identity теперь пер-устройственная, и LiveKit
      // больше не вышвыривает дубль сам) и оставляем экран с кнопкой «перенести сюда»: человек
      // в любой момент может вернуть звонок на это устройство. Если панель здесь уже закрыли
      // руками — не поднимаем её заново.
      if (params.ownerSession && params.ownerSession !== getClientSessionId() && store.call) {
        if (isCallDismissedHere(callId)) {
          store.reset();
        } else {
          store.setElsewhere({ ...store.call, token: null, acceptedAt: null });
        }
      }
      break;
  }
};
